<script lang="ts">
    import KpGenericTabGroup from 'shared/ui-widgets/tabset/KpGenericTabGroup.svelte';
    import KpTabGroupChangeListener from 'shared/ui-widgets/tabset/KpTabGroupChangeListener.svelte';
    import KpGenericTabList from 'shared/ui-widgets/tabset/KpGenericTabList.svelte';
    import KpGenericTabPanels from 'shared/ui-widgets/tabset/KpGenericTabPanels.svelte';
    import KpGenericTabItem from 'shared/ui-widgets/tabset/KpGenericTabItem.svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpGenericTabPanelContent from 'shared/ui-widgets/tabset/KpGenericTabPanelContent.svelte';
    import KpLoansActiveTable from './KpLoansActiveTable.svelte';
    import KpLoansWaitingTable from './KpLoansWaitingTable.svelte';
    import KpLoansReturnedTable from './KpLoansReturnedTable.svelte';
    import KpLoansSeekingsOfRequesterTable from './KpLoansSeekingsOfRequesterTable.svelte';
    import KpLoansSeekingsOfProviderTable from './KpLoansSeekingsOfProviderTable.svelte';
    import KpLoansSeekingsOfSeekerTable from './KpLoansSeekingsOfSeekerTable.svelte';
    import {UserRoles} from 'shared/services/current-auth.service';
    import {getLocalization} from 'core/svelte-context/context';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {strParams} from 'shared/utils/pipes';
    import type {Loan, ProvidedSeeking, SeekedSeeking, Seeking, User} from 'typings/portaro.be.types';
    import type {PageableList, PageableListState} from 'shared/utils/reactive-pageable-list';
    import type {EmptyObject} from 'typings/portaro.fe.types';
    import type {TabId} from 'shared/ui-widgets/tabset/types';

    export let user: User;
    export let activeLoanList: PageableList<Loan, string, EmptyObject>;
    export let waitingLoanList: PageableList<Loan, string, EmptyObject>;
    export let returnedLoanList: PageableList<Loan, string, EmptyObject>;
    export let seekingsOfRequesterList: PageableList<Seeking, string, EmptyObject>;
    export let seekingsOfProviderList: PageableList<SeekedSeeking, string, EmptyObject>;
    export let seekingsOfSeekerList: PageableList<ProvidedSeeking, string, EmptyObject>;
    export let activeCanLoanReturnAny: boolean;
    export let waitingCanLoanCancelAny: boolean;
    export let waitingCancelLoan: (loan: Loan) => void;
    export let activeReturnLoan: (loan: Loan) => void;
    export let activeRenewLoan: (loan: Loan) => void;

    const localize = getLocalization();

    $: activeLoanListData = activeLoanList.getState$();
    $: waitingLoanListData = waitingLoanList.getState$();
    $: returnedLoanListData = returnedLoanList.getState$();
    $: seekingsOfRequesterListData = seekingsOfRequesterList.getState$();
    $: seekingsOfProviderListData = seekingsOfProviderList.getState$();
    $: seekingsOfSeekerListData = seekingsOfSeekerList.getState$();

    $: seekingsOfRequesterVisible = !user.role.includes(UserRoles.ROLE_LIBRARY);
    $: seekingsOfProviderVisible = user.role.includes(UserRoles.ROLE_LIBRARY);
    $: seekingsOfSeekerVisible = user.role.includes(UserRoles.ROLE_LIBRARY);

    const listsLoadFunctions = {
        'tab-active-loans': () => activeLoanList.loadFirstPage(),
        'tab-waiting-loans': () => waitingLoanList.loadFirstPage(),
        'tab-returned-loans': () => returnedLoanList.loadFirstPage(),
        'tab-seekings-of-requester': () => seekingsOfRequesterList.loadFirstPage(),
        'tab-seekings-of-provider': () => seekingsOfProviderList.loadFirstPage(),
        'tab-seekings-of-seeker': () => seekingsOfSeekerList.loadFirstPage(),
    };

    const handleTabChange = (event: CustomEvent<TabId>) => {
        const loadFuncPair = Object.entries(listsLoadFunctions).find(([key,]) => event.detail === key);
        if (exists(loadFuncPair) && exists(loadFuncPair[1]) && isFunction(loadFuncPair[1])) {
            loadFuncPair[1]();
        }
    }

    function getPageableListSize(pageableListState: PageableListState<any, any, any>): string | number {
        return pageableListState?.totalSize ?? '-';
    }
</script>

<KpGenericTabGroup>
    <KpTabGroupChangeListener on:tab-change={handleTabChange}/>

    <KpGenericTabList>
        <KpGenericTabItem tabId="tab-active-loans">
            {localize(/* @kp-localization loan.Vypujcky */ 'loan.Vypujcky')}
            <span class="text-muted">{getPageableListSize($activeLoanListData)}</span>
        </KpGenericTabItem>

        <KpGenericTabItem tabId="tab-waiting-loans">
            {localize(/* @kp-localization loan.WaitingLoans */ 'loan.WaitingLoans')}
            <span class="text-muted">{getPageableListSize($waitingLoanListData)}</span>
        </KpGenericTabItem>

        <KpGenericTabItem tabId="tab-returned-loans">
            {localize(/* @kp-localization loan.Vracene */ 'loan.Vracene')}
            <span class="text-muted">{getPageableListSize($returnedLoanListData)}</span>
        </KpGenericTabItem>

        {#if seekingsOfRequesterVisible}
            <KpGenericTabItem tabId="tab-seekings-of-requester">
                {localize(/* @kp-localization loan.MVS */ 'loan.MVS')}
                <span class="text-muted">{getPageableListSize($seekingsOfRequesterListData)}</span>
            </KpGenericTabItem>
        {/if}

        {#if seekingsOfProviderVisible}
            <KpGenericTabItem tabId="tab-seekings-of-provider">
                <span class="text-ellipsis">{pipe(localize(/* @kp-localization loan.MvsZadankyNaX */ 'loan.MvsZadankyNaX'), strParams(user.sigla ?? user.text))}</span>
                <span class="text-muted">{getPageableListSize($seekingsOfProviderListData)}</span>
            </KpGenericTabItem>
        {/if}

        {#if seekingsOfSeekerVisible}
            <KpGenericTabItem tabId="tab-seekings-of-seeker">
                <span class="text-ellipsis">{pipe(localize(/* @kp-localization loan.MvsZadankyZX */ 'loan.MvsZadankyZX'), strParams(user.sigla ?? user.text))}</span>
                <span class="text-muted">{getPageableListSize($seekingsOfSeekerListData)}</span>
            </KpGenericTabItem>
        {/if}
    </KpGenericTabList>

    <KpGenericTabPanels>
        <KpGenericTabPanelContent tabPanelId="tab-panel-active-loans">
            <KpGenericPanel hasBodyPadding="{false}">
                <KpLoansActiveTable loanList="{activeLoanList}"
                                    canLoanReturnAny="{activeCanLoanReturnAny}"
                                    renewLoan="{activeRenewLoan}"
                                    returnLoan="{activeReturnLoan}"/>
            </KpGenericPanel>
        </KpGenericTabPanelContent>

        <KpGenericTabPanelContent tabPanelId="tab-panel-waiting-loans">
            <KpGenericPanel hasBodyPadding="{false}">
                <KpLoansWaitingTable loanList="{waitingLoanList}"
                                     canLoanCancelAny="{waitingCanLoanCancelAny}"
                                     cancelLoan="{waitingCancelLoan}"/>
            </KpGenericPanel>
        </KpGenericTabPanelContent>

        <KpGenericTabPanelContent tabPanelId="tab-panel-returned-loans">
            <KpGenericPanel hasBodyPadding="{false}">
                <KpLoansReturnedTable loanList="{returnedLoanList}"/>
            </KpGenericPanel>
        </KpGenericTabPanelContent>

        {#if seekingsOfRequesterVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-seekings-of-requester">
                <KpGenericPanel hasBodyPadding="{false}">
                    <KpLoansSeekingsOfRequesterTable loanList="{seekingsOfRequesterList}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if seekingsOfProviderVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-seekings-of-provider">
                <KpGenericPanel hasBodyPadding="{false}">
                    <KpLoansSeekingsOfProviderTable loanList="{seekingsOfProviderList}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}

        {#if seekingsOfSeekerVisible}
            <KpGenericTabPanelContent tabPanelId="tab-panel-detail-seekings-of-seeker">
                <KpGenericPanel hasBodyPadding="{false}">
                    <KpLoansSeekingsOfSeekerTable loanList="{seekingsOfSeekerList}"/>
                </KpGenericPanel>
            </KpGenericTabPanelContent>
        {/if}
    </KpGenericTabPanels>
</KpGenericTabGroup>